import { useRef, useEffect, useState } from 'react'
import { gsap } from '@shared/lib/gsap'
import { useAppStore } from '@shared/store'
import { getT } from '@shared/lib/i18n'
import { BUSINESS } from '@shared/config'
import { BRAND_LOGOS } from '@shared/config/products'
import * as styles from './About.css'

// ─── Constants ────────────────────────────────────────────────────────────────

// 스택(서비스) 카드 색 — kacelab 계단 팔레트
const STACK_CARD_STYLES = [
  { bg: '#141519', color: '#ffffff' },
  { bg: '#4B4C50', color: '#ffffff' },
  { bg: '#BDBEC0', color: '#222222' },
  { bg: 'var(--color-bgSecondary, #f5f5f3)', color: 'var(--color-text, #0d0d0d)' },
]

const DAYS_KO = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일']

// ─── Stacked card height helper ──────────────────────────────────────────────

const getCardHeight = (idx: number, hovered: number | null): string => {
  const defaults = ['100%', '80%', '60%', '40%']
  if (hovered === null) return defaults[idx]
  const map: Record<number, string[]> = {
    0: ['100%', '8.25rem', '4.5rem', '2.25rem'],
    1: ['100%', 'calc(100% - 3.75rem)', '6rem', '2.25rem'],
    2: ['100%', 'calc(100% - 2.25rem)', 'calc(100% - 6rem)', '3.75rem'],
    3: ['100%', 'calc(100% - 2.25rem)', 'calc(100% - 4.5rem)', 'calc(100% - 8.25rem)'],
  }
  return map[hovered][idx]
}

// ─── 소개 카드 (클린회원 로고 회색 확대 워터마크 — 글자 뒤 배치) ───────────────

function IntroCard({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={mobile ? styles.mobIntroCard : styles.introCard}>
      <img
        src="/images/클린회원_로고.png"
        alt=""
        aria-hidden="true"
        className={styles.introWatermark}
      />
      <div className={styles.introContent}>
        <span className={styles.introLabel}>About Us</span>
        <h2 className={styles.introTitle}>문 앞의 안심을{'\n'}만듭니다</h2>
        <p className={styles.introLead}>
          디지털 도어락 설치·수리부터 긴급 개방과 열쇠 복제까지, 현관 보안의 모든 것을
          책임지는 도어락·열쇠 전문 업체입니다.
        </p>
        <p className={styles.introBadgeLine}>
          (사)한국열쇠협회 클린회원 등록업체 · 추가요금을 요구하지 않습니다
        </p>
      </div>
      <div className={styles.introBottom}>
        <a className={styles.introCta} href={`tel:${BUSINESS.phone.replace(/-/g, '')}`}>
          무료 상담·견적 {BUSINESS.phone} →
        </a>
        <span className={styles.introMeta}>
          {BUSINESS.serviceArea}
          <br />
          {BUSINESS.hours}
        </span>
      </div>
    </div>
  )
}

// ─── 정찰제 카드 ──────────────────────────────────────────────────────────────

function PriceCard() {
  return (
    <div className={styles.priceCard}>
      <span className={styles.priceLabel}>Fixed Price</span>
      <div>
        <div className={styles.priceTitle}>정찰제 · 설치비 포함</div>
        <p className={styles.priceDesc}>
          표시 가격에 설치비가 포함되어 현장에서 금액이 바뀌지 않습니다.
        </p>
      </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function About() {
  const sectionRef = useRef<HTMLElement>(null)
  const leftRef = useRef<HTMLDivElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  const { isDarkMode, lang, toggleDarkMode } = useAppStore()
  const t = getT(lang)
  const [activeCard, setActiveCard] = useState<number | null>(null)
  const [time, setTime] = useState('')
  const [day, setDay] = useState('')

  // Live clock
  useEffect(() => {
    const tick = () => {
      const now = new Date()
      const hh = String(now.getHours()).padStart(2, '0')
      const mm = String(now.getMinutes()).padStart(2, '0')
      setTime(`${hh}:${mm}`)
      setDay(DAYS_KO[now.getDay()])
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  // GSAP scroll reveal
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      gsap.from([leftRef.current, rightRef.current], {
        opacity: 0,
        y: 40,
        duration: 0.8,
        stagger: 0.06,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
        },
      })

      if (bottomRef.current) {
        gsap.from(Array.from(bottomRef.current.children), {
          opacity: 0,
          y: 30,
          duration: 0.7,
          stagger: 0.06,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: bottomRef.current,
            start: 'top 80%',
          },
        })
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const allLogos = [...BRAND_LOGOS, ...BRAND_LOGOS]

  return (
    <section id="about" ref={sectionRef} className={styles.section}>
      <div className={styles.layout}>

        {/* ── LEFT COLUMN ── */}
        <div className={styles.leftCol} ref={leftRef}>

          {/* left1: floating keyword tags */}
          <div className={styles.left1}>
            {t.about.keywords.map((kw, i) => (
              <span
                key={kw}
                className={styles.keyword}
                style={{
                  animationDuration: `${2.5 + (i % 4) * 0.5}s`,
                  animationDelay: `${(i * 0.3) % 2}s`,
                }}
              >
                {kw}
              </span>
            ))}
          </div>

          {/* left2: 정찰제 카드 */}
          <PriceCard />

          {/* left3: 소개 카드 (클린회원 워터마크) */}
          <IntroCard />
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className={styles.rightCol} ref={rightRef}>

          <div className={styles.ms2Top}>

            <div className={styles.topLeft}>

              <div className={styles.topRow}>

                {/* Work card */}
                <div className={`${styles.linkItem} ${styles.workCard}`}>
                  <div
                    className={styles.bgImg}
                    style={{ backgroundImage: `url(/images/samples/opendoor.png)` }}
                  />
                  <div className={styles.bgImgOverlay} />
                  <div className={styles.cardContent}>
                    <span className={styles.cardLabel}>{t.about.workTitle}</span>
                    <div>
                      <div className={styles.cardNumber}>{t.about.workNumber}</div>
                      <div className={styles.cardTitle}>{t.about.workTitle}</div>
                    </div>
                  </div>
                </div>

                {/* Who We Are card */}
                <div className={styles.whoCard}>
                  <img
                    src={`/images/samples/b.png`}
                    alt="Who We Are"
                    className={styles.whoCardImg}
                  />
                  <span className={styles.whoCardLabel}>Who We Are</span>
                </div>
              </div>

              {/* Video card */}
              <div className={styles.videoCard}>
                <img
                  src={`/images/samples/bluegreen.png`}
                  alt="Portfolio"
                  className={styles.videoCardImg}
                />
                <div className={styles.videoCardOverlay} />
              </div>
            </div>

            <div className={styles.topRight}>

              {/* Culture card */}
              <div className={`${styles.linkItem} ${styles.cultureCard}`}>
                <div
                  className={styles.bgImg}
                  style={{ backgroundImage: `url(/images/samples/3d-render-glassy-greenwebp.webp)` }}
                />
                <div className={styles.bgImgOverlay} />
                <div className={styles.cardContent}>
                  <span className={styles.cardLabel}>{t.about.cultureTitle}</span>
                  <div>
                    <div className={styles.cardNumber}>{t.about.cultureNumber}</div>
                    <div className={styles.cardTitle}>{t.about.cultureTitle}</div>
                  </div>
                </div>
              </div>

              {/* Stacked cards = 제공 서비스 */}
              <div className={styles.stackedCards}>
                {t.about.stackCards.map((card, i) => (
                  <div
                    key={card.title}
                    className={styles.stackCard}
                    style={{
                      backgroundColor: STACK_CARD_STYLES[i].bg,
                      color: STACK_CARD_STYLES[i].color,
                      height: getCardHeight(i, activeCard),
                      zIndex: i + 1,
                    }}
                    onMouseEnter={() => setActiveCard(i)}
                    onMouseLeave={() => setActiveCard(null)}
                  >
                    {/* 원본 방식: hover 중에는 활성 카드의 텍스트만 표시 */}
                    <div
                      className={styles.stackCardContent}
                      style={{ opacity: activeCard === null || activeCard === i ? 1 : 0 }}
                    >
                      <div className={styles.stackCardTitle} style={{ color: STACK_CARD_STYLES[i].color }}>
                        {card.title}
                      </div>
                      <div className={styles.stackCardDesc} style={{ color: STACK_CARD_STYLES[i].color }}>
                        {card.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ms2Bottom */}
          <div className={styles.ms2Bottom} ref={bottomRef}>

            <div className={styles.bottomTime}>
              <div className={styles.clockDay}>{day}</div>
              <div className={styles.clockTime}>{time}</div>
            </div>

            <div className={styles.bottomMarquee}>
              <div className={styles.marqueeTrack}>
                {allLogos.map((logo, i) => (
                  <div key={`${logo.name}-${i}`} className={styles.marqueeItemStyle}>
                    <img src={logo.image} alt={logo.name} className={styles.marqueeImg} />
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.bottomDark} onClick={toggleDarkMode}>
              <span className={styles.bottomDarkLabel}>{t.about.theme}</span>
              <span className={styles.bottomDarkIcon}>{isDarkMode ? '☀️' : '🌙'}</span>
              <span className={styles.bottomDarkTitle}>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE layout (≤1024px) — kacelab mob_view 방식: 별도 구성으로 전환 ── */}
      <div className={styles.mobLayout}>

        {/* ① 소개 카드 (클린회원 워터마크) */}
        <IntroCard mobile />

        {/* ② 정찰제 카드 */}
        <PriceCard />

        {/* ③ Work(60%) + Who 이미지(40%) 행 */}
        <div className={styles.mobTopRow}>
          <div className={styles.mobWorkItem}>
            <div className={`${styles.linkItem} ${styles.workCard}`}>
              <div className={styles.cardContent}>
                <span className={styles.cardLabel}>{t.about.workTitle}</span>
                <div>
                  <div className={styles.cardNumber}>{t.about.workNumber}</div>
                  <div className={styles.cardTitle}>{t.about.workTitle}</div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.mobWhoItem}>
            <img src={`/images/samples/b.png`} alt="Who We Are" className={styles.whoCardImg} />
          </div>
        </div>

        {/* ④ Culture 카드 */}
        <div className={`${styles.linkItem} ${styles.cultureCard}`}>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t.about.cultureTitle}</span>
            <div>
              <div className={styles.cardNumber}>{t.about.cultureNumber}</div>
              <div className={styles.cardTitle}>{t.about.cultureTitle}</div>
            </div>
          </div>
        </div>

        {/* ⑤ 비디오 카드 + Who We Are 라벨 */}
        <div className={styles.mobVideoCard}>
          <img src={`/images/samples/bluegreen.png`} alt="Portfolio" className={styles.videoCardImg} />
          <div className={styles.videoCardOverlay} />
          <span className={styles.mobVideoLabel}>Who We Are</span>
        </div>

        {/* ⑥ 서비스 스택 — hover 대신 고정 계단 높이 상시 노출 */}
        <div className={styles.mobStack}>
          {t.about.stackCards.map((card, i) => (
            <div
              key={card.title}
              className={styles.mobStackCard}
              style={{
                backgroundColor: STACK_CARD_STYLES[i].bg,
                color: STACK_CARD_STYLES[i].color,
                // 컨테이너(mobStack) 높이 대비 비율 — 브레이크포인트별 컨테이너
                // 높이만 조정하면 계단이 통째로 스케일된다.
                height: ['100%', '77.5%', '55%', '32.5%'][i],
                zIndex: i + 1,
              }}
            >
              <div className={styles.stackCardContent}>
                <div className={styles.stackCardTitle} style={{ color: STACK_CARD_STYLES[i].color }}>
                  {card.title}
                </div>
                <div className={styles.stackCardDesc} style={{ color: STACK_CARD_STYLES[i].color }}>
                  {card.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ⑦ 브랜드 마키 */}
        <div className={styles.mobMarquee}>
          <div className={styles.marqueeTrack}>
            {allLogos.map((logo, i) => (
              <div key={`m-${logo.name}-${i}`} className={styles.marqueeItemStyle}>
                <img src={logo.image} alt={logo.name} className={styles.marqueeImg} />
              </div>
            ))}
          </div>
        </div>

        {/* ⑧ 다크모드 카드 */}
        <div className={styles.mobDark} onClick={toggleDarkMode}>
          <span className={styles.bottomDarkLabel}>{t.about.theme}</span>
          <span className={styles.bottomDarkIcon}>{isDarkMode ? '☀️' : '🌙'}</span>
          <span className={styles.bottomDarkTitle}>
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </span>
        </div>

      </div>
    </section>
  )
}

export default About
